import { useEffect, useState } from "react";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../../components/recharts/functions";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import { BrushStart, ChartType } from "../../../../models/chart_model";
import { renderHourTick } from "../../../../components/recharts/components";
import { useSelector } from "react-redux";
import loader from "../../../../components/recharts/loader.gif";
import { Download } from "lucide-react";
import { getAccessToken } from "../../../Protected";
import { downloadFile } from "../../../../components/DownloadFile";
import { BASE_URL } from "../../../../configs";
import { ErrorPopup } from "../../../../components/ErrorHandling";
import { normalizeDisplayLabelWithoutUndefined } from "../../../../utility/displayText";

export default function BuyBidSimulationAnalysis({ data }: { data: any[] }) {
  const chartSuffixes = [
    {
      suffix: "quantum",
      displayName: "Quantum (MW)",
      stackSuffix: "quantum",
      stroke: "rgba(249, 212, 190, 1)",
      legendColor: "rgba(255, 180, 137, 1)",
      chartType: ChartType.Bar,
      unit: "MW",
      yAxisId: "left",
    },
    {
      suffix: "price",
      displayName: "Rate (₹/kWh)",
      stackSuffix: "price",
      chartType: ChartType.Line,
      stroke: "rgba(236, 28, 36, 1)",
      unit: "₹/KWh",
      yAxisId: "right",
    },
  ];
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [selectedMetrics] = useState<any[]>([
    chartSuffixes[0],
    chartSuffixes[1],
  ]);
  const [error, setError] = useState<string | null>(null);
  const preSelectedDate = useSelector(
    (state: any) => state.modDemoDate.selectedDate
  );
  const modSelectedSession = useSelector(
    (state: any) => state.modDemoSession.modSession
  );

  useEffect(() => {
    let filteredData = data.map((item) => ({
      ...item,
      date: preSelectedDate,
    }));
    const par: ChartDataParams = {
      keys: ["date", "time_slot__slot"],
      tableKeys: ["date", "time_slot__slot"],
      groupBy: [],
      volumeKey: ``,
      aggregationKeys: ["quantum"],
      average: true,
      averageKeys: ["price"],
      averageDuplicationKeys: ["date", "time_slot__slot", "generator"],
      priceKey: "",
      data: filteredData,
    };

    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
  }, [data, preSelectedDate]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string,
    stroke: string,
    legendColor: string
  ) => {
    return {
      dataKey: `${key}_${suffix}`,
      name: `${normalizeDisplayLabelWithoutUndefined(key)} ${displayName}`,
      type: ["price"].includes(suffix) ? ChartType.Line : ChartType.Bar,
      unit: unit,
      stroke: stroke,
      legendColor: legendColor,
      yAxisId: yAxisId,
    };
  };

  function getLegends() {
    return selectedMetrics.flatMap(
      ({
        suffix,
        displayName,
        stackSuffix,
        chartType,
        unit,
        yAxisId,
        stroke,
        legendColor,
      }) =>
        chartKeys.map(
          (key, index) =>
            createChartObject(
              key,
              index,
              suffix,
              displayName,
              stackSuffix,
              chartType,
              unit,
              yAxisId,
              stroke,
              legendColor
            ) as any
        )
    );
  }

  const downloadTable = async () => {
    setError(null);
    await downloadFile({
      url: `${BASE_URL}/mod_demo/getBidDownload?date=${preSelectedDate}&session=${modSelectedSession}`,
      filename: `Demo_Bid_Table_${preSelectedDate}.xlsx`,
      headers: {
        Authorization: `${getAccessToken() || ""}`,
      },
      onSuccess: () => setLoading(false),
      onError: (err) => {
        setError("Failed to download Excel file. Please try again later.");
      },
    });
  };

  return (
    <>
      <div className="dash-card col-span-1 md:col-span-12 border border-[#718EBF1C] !bg-[#fff] !shadow-[rgba(149,157,165,0.2)_0px_8px_24px] !min-h-[100%] w-full">
        <div className="flex w-full justify-center items-center">
          <h2 className="text-center chartHeading w-full "> Buy Bids (DAM) </h2>
          <button
            onClick={downloadTable}
            className="flex items-center gap-2 p-2 text-sm rounded-xl bg-[#FFFAF6] text-[#F1935C] font-medium border border-[#F1935C]"
          >
            {/* <span>Download Data</span> */}
            <Download className="w-5 h-5" />
          </button>
        </div>
        {loading ? (
          <div className="h-full w-full bg-white flex items-center justify-center">
            <img
              src={loader}
              width={200}
              alt="loader"
              className="block mt-[5rem]"
            />
          </div>
        ) : (
          <>
            {data.length === 0 ? (
              <>
                <div className=" flex items-center justify-center overflow-auto capitalize text-nowrap rounded-lg w-full h-full">
                  <h1 className="chartHeading !text-base text-center !font-medium w-100">
                    No Buy Bids Available...
                  </h1>
                </div>
              </>
            ) : (
              <>
                <ReMixChart
                  chartHeight="calc(100% - 50px)"
                  data={chartData}
                  showBrush={false}
                  brushStart={BrushStart.Start}
                  // maxYsize={2000}
                  isTimeSlot={true}
                  onlyTitle={true}
                  legends={getLegends()}
                  yAxisLabel="MW"
                  secondYAxisLabel="₹/KWh"
                  nameGap={40}
                  xDataKey={"time_slot__slot"}
                  top={10} // Top margin for grid
                  bottom={30} // Bottom margin for grid
                  xTick={(value: any, index: any) => {
                    return renderHourTick(value, 10);
                  }}
                  secondXDataKey={"date"}
                  showToolbox={false}
                />
                <ErrorPopup error={error} onClose={() => setError(null)} />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
